import styled from "@emotion/styled";
import { EventPlatformEntry } from "@kaltura/mediaspace-shared-types";
import { MediaGrid } from "@kaltura/mediaspace-shared-media-grid";
import { Box, systemWidth } from "@kaltura/mediaspace-shared-styled";
import { Theme } from "@mui/material";
import { useMediaQuery } from "@kaltura/mediaspace-shared-utils";
import { Typography } from "@kaltura/ds-react-components";
import { Truncate } from "@kaltura/mediaspace-shared-ui";
import { useButtonAnalytics } from "@kaltura/mediaspace-shared-hooks";
import { ButtonClickAnalyticsType } from "@kaltura/mediaspace-shared-types";
import { HtmlReactParser } from "@kaltura/mediaspace-shared-utils";

export interface PlaylistsGridProps {
    title: string;
    description?: string;
    entries?: EventPlatformEntry[];
    categoryId?: number; // if watching the playlist in the context of a category
    playlistId?: string;
    page?: number;
}

const StyledPlaylistsGrid = styled(Box)(({ theme }) => ({
    "&": systemWidth({ theme }),
    marginBottom: theme.spacing(4),
    [theme.breakpoints.down(theme.breakpoints.values.md)]: {
        marginBottom: theme.spacing(3),
    },
}));

const StyledDescription = styled(Typography)(({ theme }) => ({
    color: theme.kaltura.palette.tone1,
    marginBottom: theme.spacing(4),
    maxWidth: 790,
    [theme.breakpoints.down(theme.breakpoints.values.md)]: {
        marginBottom: theme.spacing(3),
        maxWidth: "100%",
    },
}));

export const PlaylistsGrid = (props: PlaylistsGridProps) => {
    const { title, description, categoryId, playlistId, entries = [], page } = props;

    const isSmallScreen = useMediaQuery((theme: Theme) => theme.breakpoints.down(theme.breakpoints.values.md));

    const sendButtonAnalytics = useButtonAnalytics();

    const handleShowMore = (showMore: boolean) => {
        const actionName = showMore ? "Show less description" : "Show more description";
        const buttonType = showMore ? ButtonClickAnalyticsType.COLLAPSE : ButtonClickAnalyticsType.EXPAND;
        if (playlistId) {
            sendButtonAnalytics(actionName, buttonType, playlistId);
        }
        else {
            sendButtonAnalytics(actionName, buttonType);
        }
    };

    return (
        <StyledPlaylistsGrid>
            <Typography component={"h1"} variant={isSmallScreen ? "h2" : "h1"}>
                {title}
            </Typography>
            {description && (
                <StyledDescription variant={"body1"}>
                    <Truncate lines={3} onToggle={handleShowMore}>
                        {HtmlReactParser(description)}
                    </Truncate>
                </StyledDescription>
            )}
            <MediaGrid
                categoryId={categoryId}
                playlistId={playlistId}
                content={entries}
                analyticsValue={"Playlist Grid - Thumbnail link"}
                showPagination={true}
                itemsPerPage={50}
                page={page}
            />
        </StyledPlaylistsGrid>
    );
};

export default PlaylistsGrid;
